<template>
  <div class="faq-page">
    <div class="main-content">
      <div class="content-card">
        <v-expansion-panels
          v-model="openedPanel"
          variant="accordion"
          class="faq-panels"
        >
          <v-expansion-panel
            v-for="n in 9"
            :key="n"
            class="faq-panel"
            :aria-labelledby="`faq-question-${n}`"
          >
            <v-expansion-panel-title
              :id="`faq-question-${n}`"
            >
              <span class="faq-question">
                <strong class="question-number">{{ n }}.</strong>
                {{ t("faq.pregunta" + n) }}
              </span>
            </v-expansion-panel-title>

            <v-expansion-panel-text v-if="n === 6">
              <div class="faq-answer">
                <UmbralesContenido />
              </div>
            </v-expansion-panel-text>

            <v-expansion-panel-text v-else>
              <div
                class="faq-answer"
                v-html="respuestas[n - 1]"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { computed, ref, watch, nextTick, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { useHead } from "@unhead/vue";
import { getLocaleFromRoute } from "@/router";
import UmbralesContenido from "@/components/Estadisticas/UmbralesContenido.vue";

const openedPanel = ref(null);
const pendingTimeouts = [];
const pendingAnimationFrames = [];
const route = useRoute();
const { t } = useI18n();

const currentLocale = computed(() => getLocaleFromRoute(route));

// Función para limpiar HTML y obtener texto plano
const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replaceAll(/<br\s{0,5}\/?>/gi, " ")
    .replaceAll(/<[^>]{0,500}>/g, "")
    .replaceAll(/\s{1,80}/g, " ")
    .trim();
};

// FAQ Schema JSON-LD para Google Rich Results
const faqStructuredData = computed(() => {
  const baseUrl = "https://calidadtelecos.cnmc.gob.es";
  const faqUrl =
    currentLocale.value === "en"
      ? `${baseUrl}/en/faq`
      : `${baseUrl}/preguntas-frecuentes`;

  const faqItems = [];
  for (let i = 1; i <= 9; i++) {
    const question = t(`faq.pregunta${i}`);
    const answer =
      i === 6
        ? t("faq.respuesta6_schema", t("seo.descriptions.umbrales"))
        : stripHtml(t(`faq.respuesta${i}`));

    if (question && answer) {
      faqItems.push({
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
      });
    }
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems,
    url: faqUrl,
    inLanguage: currentLocale.value === "en" ? "en-US" : "es-ES",
    isPartOf: {
      "@type": "WebSite",
      name:
        currentLocale.value === "en"
          ? "CNMC Speed Test"
          : "Test de Velocidad CNMC",
      url: baseUrl,
    },
  };
});

useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: computed(() => JSON.stringify(faqStructuredData.value)),
    },
  ],
});

const legalUrl = computed(() =>
  currentLocale.value === "en" ? "/en/legal-information" : "/informacion-legal"
);

const respuestas = computed(() => {
  return Array.from({ length: 9 }, (_, i) => {
    let html = t("faq.respuesta" + (i + 1));
    html = html
      .replaceAll(
        /href=['"]\/informacionTCalidad['"]/g,
        `href="${legalUrl.value}"`
      )
      .replaceAll(
        /href=['"]\/en\/informacionTCalidad['"]/g,
        `href="${legalUrl.value}"`
      )
      .replaceAll(/href=['"]\/informacion-legal['"]/g, `href="${legalUrl.value}"`)
      .replaceAll(
        /href=['"]\/en\/legal-information['"]/g,
        `href="${legalUrl.value}"`
      );
    return html;
  });
});

onBeforeUnmount(() => {
  pendingTimeouts.forEach((id) => clearTimeout(id));
  pendingAnimationFrames.forEach((id) => cancelAnimationFrame(id));
  pendingTimeouts.length = 0;
  pendingAnimationFrames.length = 0;
});

watch(
  () => route.query.open,
  async (value) => {
    if (!value) return;

    const index = Number(value) - 1;
    openedPanel.value = index;

    await nextTick();

    const raf1 = requestAnimationFrame(() => {
      const timeout1 = setTimeout(() => {
        const raf2 = requestAnimationFrame(() => {
          const timeout2 = setTimeout(() => {
            const el = document.getElementById(`faq-question-${value}`);
            if (!el) return;

            const y =
              el.getBoundingClientRect().top +
              window.pageYOffset -
              120;

            window.scrollTo({
              top: y,
              behavior: "smooth",
            });
          }, 300);
          pendingTimeouts.push(timeout2);
        });
        pendingAnimationFrames.push(raf2);
      }, 300);
      pendingTimeouts.push(timeout1);
    });
    pendingAnimationFrames.push(raf1);
  },
  { immediate: true }
);
</script>

<style scoped>
.faq-page {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.main-content {
  padding: 16px 0 24px;
}

/* ===== Content Card (mismo layout que otras estáticas) ===== */
.content-card {
  background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px;
}

/* ===== Panels container ===== */
.faq-panels {
  background: transparent;
}

/* ===== Individual panel — sin borde, divisor inferior ===== */
.faq-panel {
  background: transparent !important;
  border-radius: 0 !important;
  border: none;
  border-bottom: 1px solid #e8ecf1;
  transition: background 0.2s ease;
}

.faq-panel:first-child {
  border-top: 1px solid #e8ecf1;
}

.faq-panel::before {
  box-shadow: none !important;
}

.faq-panel :deep(.v-expansion-panel__shadow) {
  display: none;
}

/* Hover sutil */
.faq-panel:hover {
  background: rgba(var(--v-theme-primary), 0.02) !important;
}

/* Panel abierto — acento izquierdo */
.faq-panel :deep(.v-expansion-panel-title--active) {
  border-left: 3px solid rgb(var(--v-theme-primary));
  margin-left: -1px;
}

/* ===== Pregunta ===== */
.faq-question {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.5;
}

.question-number {
  color: rgb(var(--v-theme-primary));
  margin-right: 6px;
  font-size: 0.9375rem;
}

/* ===== Respuesta ===== */
.faq-answer {
  font-size: 0.875rem;
  line-height: 1.75;
  color: #475569;
  padding-left: 2px;
}

.faq-answer :deep(a) {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid transparent;
  transition: border-color 0.15s;
}

.faq-answer :deep(a:hover) {
  border-bottom-color: rgb(var(--v-theme-primary));
}

.faq-answer :deep(p) {
  margin-bottom: 12px;
}

.faq-answer :deep(p:last-child) {
  margin-bottom: 0;
}

.faq-answer :deep(ul),
.faq-answer :deep(ol) {
  margin-left: 20px;
  margin-bottom: 12px;
}

.faq-answer :deep(li) {
  margin-bottom: 6px;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .main-content {
    padding: 16px;
  }

  .content-card {
    padding: 20px;
  }
}

@media (max-width: 600px) {
  .content-card {
    padding: 16px;
  }

  .faq-question {
    font-size: 0.875rem;
  }

  .faq-answer {
    font-size: 0.8125rem;
  }
}
</style>
